import { useEffect, useState } from 'react'
import { get, isEmpty } from 'lodash'
import { useQuery } from 'react-query'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material'

import { ConfirmationDialog, UserAvatar } from 'components'
import { API } from 'core/requests'
import { t } from 'i18n'
import { getUserFullName } from 'utils/user'

export default function SurveyOverviewRemoveEmployee({
  isDialogOpen,
  setIsDialogOpen,
  removeEmployees,
  survey,
  isRemoveEmployeeLoading,
}) {
  const [selectedEmployees, setSelectedEmployees] = useState<any[]>([])
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const { data: employees, isLoading: employeesIsLoading } = useQuery(
    'employees',
    () => API.get('employeesAutocomplete'),
    { enabled: isDialogOpen },
  )

  useEffect(() => {
    if (!isDialogOpen) {
      setSelectedEmployees([])
      setConfirmDialogOpen(false)
    }
  }, [isDialogOpen])

  const handleClose = () => {
    setIsDialogOpen(false)
  }

  return (
    <Dialog
      open={isDialogOpen}
      maxWidth='sm'
      fullWidth
      onClose={handleClose}
      aria-labelledby='remove-employees-dialog-title'
      aria-describedby='remove-employees-dialog-description'
    >
      <DialogTitle id='remove-employees-dialog-title'>
        {t('surveyOverview.employeesRemoval.removeEmployees')}
      </DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <Typography variant='body2' color='text.secondary' paragraph>
            {t('surveyOverview.employeesRemoval.description', {
              surveyName: get(survey, 'name'),
            })}
          </Typography>
          <Typography variant='body2' color='error' paragraph>
            {t('surveyOverview.employeesRemoval.warning')}
          </Typography>
        </Grid>
        <Autocomplete
          multiple
          id='surveyParticipants'
          value={selectedEmployees}
          onChange={(event: any, newValue: any | null) => {
            setSelectedEmployees(newValue || [])
          }}
          autoHighlight
          loading={employeesIsLoading}
          options={employees || []}
          getOptionLabel={employee => getUserFullName(employee)}
          renderOption={(props, employee: any) => (
            <Box
              component='li'
              sx={{ '& > img': { mr: 3, flexShrink: 0 } }}
              {...props}
            >
              <UserAvatar
                userId={get(employee, 'id')}
                name={get(employee, 'first_name')}
                src={get(employee, 'avatar.url')}
                size={24}
              />
              <Box component='span' ml={1}>
                {getUserFullName(employee)}
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t('surveyOverview.employeesRemoval.textFieldLabel')}
              variant='outlined'
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          {t('surveyOverview.employeesRemoval.cancelButton')}
        </Button>
        <Button
          onClick={() => setConfirmDialogOpen(true)}
          color='primary'
          disabled={
            isRemoveEmployeeLoading ||
            isEmpty(selectedEmployees) ||
            !get(survey, 'id')
          }
          autoFocus
        >
          {isRemoveEmployeeLoading ? (
            <CircularProgress size={24} />
          ) : (
            t('surveyOverview.employeesRemoval.removeButton')
          )}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        dialogOpen={confirmDialogOpen}
        setDialogOpen={setConfirmDialogOpen}
        message={t('surveyOverview.employeesRemoval.confirmMessage', {
          count: selectedEmployees.length,
          surveyName: get(survey, 'name'),
        })}
        item={null}
        actionProps={{
          onClick: () => {
            setConfirmDialogOpen(false)
            removeEmployees({
              survey_id: get(survey, 'id'),
              user_ids: selectedEmployees.map(employee => employee.id),
            })
          },
          isLoading: isRemoveEmployeeLoading,
          label: t('surveyOverview.employeesRemoval.confirmButton'),
          color: 'error',
        }}
      />
    </Dialog>
  )
}
